import { useCallback, useEffect, useRef, useState } from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import { isEmpty } from "lodash";

import { useAppState } from "core/contexts/AppContext";
import {
    getAuthExpiry,
    hasTokenExpired, logout
} from "core/includes/auth";
import { GET_USER } from "core/includes/queries";
import { hasFeatureFlag } from "core/includes/site";
import { FLAG_SHOP_ONLINE_VIDEOS } from "core/includes/featureFlags";

export type Subscription = {
    id: string,
    NiceCurrentPeriodEnd: string,
    IsValid: boolean,
    Quantity: number,
    PaymentIntent: string,
    Price: string,
    ProductName: string,
    SubscriptionID: string,
    PaymentStatus: string,
    CancelStatus: string,
    CancelScheduleRequestedAt: string,
    BillingHistory: [BillingHistory],
};

export type BillingHistory = {
    id: string,
    InvoiceID: string,
    Currency: string,
    Amount: number,
    Card: string,
    CardType: string,
    Created: string,
    ReceiptURL: string,
};

const UserSession: any = () => {
    const [appState, setAppState] = useAppState();
    const [noUser, setNoUser] = useState(false);
    const noUserRef = useRef(noUser);
    const user = appState?.user;
    const resolveRef = useRef<any>();
    const rejectRef = useRef<any>();

    const onCompleted = (data: any) => {
        if (resolveRef && resolveRef.current) resolveRef.current(data?.getCorporateSiteUser);
    }
    const onError = (error: any) => {
        if (rejectRef && rejectRef.current) rejectRef.current(error);
    }

    const [getData, { called, data, loading, refetch }] = useLazyQuery(GET_USER, {
        onCompleted: onCompleted,
        onError: onError,
        nextFetchPolicy: "cache-first",
        context: {
            clientName: "auth",
        }
    });

    const hasShopOnlineVideosFeatureFlag = hasFeatureFlag(appState.featureFlags, FLAG_SHOP_ONLINE_VIDEOS);

    const getUser = useCallback(() => {
        return new Promise((resolve, reject) => {
            if (resolveRef) resolveRef.current = resolve;
            if (rejectRef) rejectRef.current = reject;
            return getData();
        });
    }, [getData]);

    const refetchUser = useCallback(() => {
        return refetch
            ? refetch()
                .then(response => response?.data?.getCorporateSiteUser)
                .catch(response => response?.errors)
            : getUser();
    }, [refetch, getUser]);

    /**
     * Clears the user.
     */
    const clearUser = useCallback(() => {
        return new Promise((resolve: any, reject) => {
            setAppState({
                user: null,
                authorised: false,
            });
            logout();
            resolve();
        });
    }, [setAppState]);

    useEffect(() => {
        noUserRef.current = noUser;
    }, [noUser]);

    // if no user, or the token has changed then refetch the user
    useEffect(() => {
        if (!appState.isLoadingFeatureFlags) {
            if (!isEmpty(getAuthExpiry()) && !user && !hasTokenExpired() && hasShopOnlineVideosFeatureFlag) {
                getData();
                setNoUser(false);
            } else {
                setNoUser(true);
            }
        }
    }, [user, getData, hasShopOnlineVideosFeatureFlag, appState.isLoadingFeatureFlags]);

    // set user if fetched data changes
    useEffect(() => {
        const userData = data?.getCorporateSiteUser;
        setAppState({
            clearUser: clearUser,
            getUser: getUser,
            refetchUser: refetchUser,
            user: userData,
            authorised: !!userData,
            hasUserBeenLoaded: !!userData || (called && !loading) || noUserRef.current,
        });
    }, [clearUser, data, getUser, refetch, setAppState, called, loading, refetchUser]);
    return null;
};

export default UserSession;

